/**
 * Utilities for encoding / decoding the multi-group contract reference
 * used by reown-based contract types.
 *
 * Encoding format (single string stored in `contract_reference`):
 *   ref1|;|qty1|;;|ref2|;|qty2|;;|ref3|;|qty3
 *
 *  - `|;;|` separates groups
 *  - `|;|`  separates the reference from the qty within a group
 */

import { ContractType } from "@/lib/api/otc";

// ---------------------------------------------------------------------------
// Delimiters
// ---------------------------------------------------------------------------

const GROUP_SEP = "|;;|";
const FIELD_SEP = "|;|";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ContractRefGroup {
  ref: string;
  qty: string;
}

// ---------------------------------------------------------------------------
// Reown detection
// ---------------------------------------------------------------------------

const REOWN_TYPES: Set<string> = new Set([
  ContractType.BasePriceReown,
  ContractType.RangePriceReown,
  ContractType.ReOwnWDU,
]);

export function isReownContractType(contractType: string | undefined): boolean {
  if (!contractType) return false;
  return REOWN_TYPES.has(contractType);
}

// ---------------------------------------------------------------------------
// Parse
// ---------------------------------------------------------------------------

/**
 * Parse a serialized `contract_reference` string into groups.
 *
 * Handles:
 * - New multi-group format (`ref|;|qty|;;|ref2|;|qty2`)
 * - Legacy plain strings (returns a single group with the whole string as ref, qty empty)
 * - Empty/undefined (returns one empty group)
 */
export function parseContractRefGroups(raw: string | undefined): ContractRefGroup[] {
  if (!raw || raw.trim() === "") {
    return [{ ref: "", qty: "" }];
  }

  // If the string contains the group separator, it's the new format
  if (raw.includes(GROUP_SEP) || raw.includes(FIELD_SEP)) {
    const groups = raw.split(GROUP_SEP);
    return groups.map((g) => {
      const parts = g.split(FIELD_SEP);
      return {
        ref: (parts[0] ?? "").trim(),
        qty: (parts[1] ?? "").trim(),
      };
    });
  }

  // Legacy plain string — entire value goes into the first ref, qty empty
  return [{ ref: raw, qty: "" }];
}

// ---------------------------------------------------------------------------
// Serialize
// ---------------------------------------------------------------------------

/**
 * Serialize groups back to a single string for `contract_reference`.
 *
 * Drops trailing empty groups (groups where both ref and qty are empty).
 */
export function serializeContractRefGroups(groups: ContractRefGroup[]): string {
  // Filter out completely empty trailing groups
  const trimmed = [...groups];
  while (
    trimmed.length > 0 &&
    trimmed[trimmed.length - 1].ref.trim() === "" &&
    trimmed[trimmed.length - 1].qty.trim() === ""
  ) {
    trimmed.pop();
  }

  if (trimmed.length === 0) return "";

  return trimmed
    .map((g) => `${g.ref.trim()}${FIELD_SEP}${g.qty.trim()}`)
    .join(GROUP_SEP);
}

// ---------------------------------------------------------------------------
// Display formatting (for table columns)
// ---------------------------------------------------------------------------

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumSignificantDigits: 8,
  maximumFractionDigits: 4,
});

/**
 * Format a qty number into a compact human-readable string.
 * e.g. 10000 → "10K", 4500 → "4.5K", 1200000 → "1.2M", 500 → "500"
 */
function formatCompactQty(raw: string): string {
  const num = Number(raw);
  if (isNaN(num)) return raw;
  return compactFormatter.format(num);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface ContractRefGroupError {
  ref?: string;
  qty?: string;
}

/**
 * Validate contract reference groups.
 *
 * Rules:
 * - The first group's ref and qty are both required.
 * - For any subsequent group, if either field has a value the other is required too.
 *
 * Returns an array parallel to `groups` with per-field error messages,
 * or `null` if everything is valid.
 */
export function validateContractRefGroups(groups: ContractRefGroup[]): ContractRefGroupError[] | null {
  const errors: ContractRefGroupError[] = groups.map(() => ({}));
  let hasError = false;

  for (let i = 0; i < groups.length; i++) {
    const ref = groups[i].ref.trim();
    const qty = groups[i].qty.trim();

    if (i === 0) {
      // First group: both fields required
      if (!ref) {
        errors[i].ref = "Required";
        hasError = true;
      }
      if (!qty) {
        errors[i].qty = "Required";
        hasError = true;
      }
    } else {
      // Subsequent groups: if either is filled, both are required
      if (ref && !qty) {
        errors[i].qty = "Required";
        hasError = true;
      }
      if (!ref && qty) {
        errors[i].ref = "Required";
        hasError = true;
      }
    }
  }

  return hasError ? errors : null;
}

/**
 * Strip multi-group delimiters from a serialized `contract_reference`,
 * returning just the reference strings joined by ", ".
 * If the value has no delimiters it is returned as-is.
 */
export function stripContractRefDelimiters(raw: string | undefined): string {
  if (!raw || raw.trim() === "") return "";

  if (!raw.includes(GROUP_SEP) && !raw.includes(FIELD_SEP)) {
    return raw;
  }

  const groups = parseContractRefGroups(raw);
  return groups
    .map((g) => [g.ref.trim(), g.qty.trim()].join(" "))
    .filter(Boolean)
    .join(", ");
}

/**
 * Format a serialized `contract_reference` for display in a table cell.
 *
 * - Multi-group reown format: "REF1 10k | REF2 5k | REF3 4.5k"
 * - Plain legacy string: returned as-is
 */
export function formatContractReferenceDisplay(raw: string | undefined | null): string {
  if (!raw || raw.trim() === "") return "";

  // Not multi-group format — return as-is
  if (!raw.includes(GROUP_SEP) && !raw.includes(FIELD_SEP)) {
    return raw;
  }

  const groups = parseContractRefGroups(raw);
  return groups
    .filter((g) => g.ref.trim() !== "" || g.qty.trim() !== "")
    .map((g) => {
      const ref = g.ref.trim();
      const qty = g.qty.trim();
      if (!qty) return ref;
      if (!ref) return formatCompactQty(qty);
      return `${ref} ${formatCompactQty(qty)}`;
    })
    .join(" | ");
}
